/**
 * Main Layout Component for WoodAlps Website
 * 
 * This component provides the consistent layout structure for all pages:
 * - Header with navigation and branding
 * - Main content area for page-specific content
 * - Footer with company information and links
 * - Dynamic site settings from Sanity CMS
 * - SEO meta tags and page titles
 * 
 * Features:
 * - Responsive design for all screen sizes
 * - Dynamic header transparency based on page needs
 * - Sanity CMS integration for logo and branding
 * - Fallback values for offline/loading states
 * 
 * @description Main layout wrapper providing consistent site structure and branding
 */

import { useState, useEffect } from 'react';
import Navbar from './Navbar';
import Footer from './Footer';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { getSiteSettings } from '../../src/app/studio/lib/sanity';

const Layout = ({ children, title = 'WoodAlps - Wooden Bikes Made in the Alps', transparentHeader = false, headerShadow }) => {
  const router = useRouter();
  const isHomePage = router.pathname === '/';
  
  // Site settings with fallback defaults for reliability
  const [siteSettings, setSiteSettings] = useState({
    siteName: 'WoodAlps',          // Default site name
    logo: null,                   // Will be populated from Sanity
    logoWidth: 150,               // Default logo dimensions
    logoHeight: 40,
    footerLogoWidth: 200,         // Footer logo sizing
    footerLogoHeight: 50
  });

  // Fetch dynamic site settings from Sanity CMS
  useEffect(() => {
    const fetchSiteSettings = async () => {
      try {
        const settings = await getSiteSettings();
        if (settings) {
          setSiteSettings({
            ...siteSettings, // Preserve defaults for missing properties
            ...settings      // Override with CMS data
          });
        }
      } catch (error) {
        console.error('Error fetching site settings:', error);
        // Continue with default settings if fetch fails
      }
    };

    fetchSiteSettings();
  }, []);

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content="Handcrafted wooden bicycles made in the Alps, combining traditional craftsmanship with modern technology." />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        
        {/* Standard favicons - Chrome prioritizes these in this order */}
        <link rel="icon" type="image/x-icon" href="/favicon.ico?v=2" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png?v=2" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png?v=2" />
        
        {/* Apple Touch Icons */}
        <link rel="apple-touch-icon" href="/apple-touch-icon.png?v=2" />
        <link rel="apple-touch-icon-precomposed" href="/apple-touch-icon-precomposed.png?v=2" />
        
        {/* Android/PWA support */}
        <link rel="manifest" href="/manifest.json?v=2" />
        <meta name="theme-color" content="#563E2F" />
        
        {/* Microsoft Tiles */}
        <meta name="msapplication-config" content="/browserconfig.xml?v=2" />
        <meta name="msapplication-TileColor" content="#563E2F" />
        <meta name="msapplication-TileImage" content="/images/icons/ms-icon-150x150.png?v=2" />
      </Head>
      <div className="flex flex-col min-h-screen">
        <Navbar transparent={transparentHeader} headerShadow={headerShadow} siteSettings={siteSettings} />
        <main className={`flex-grow ${!transparentHeader ? 'mt-[12vh] md:mt-[12vh]' : ''}`}>{children}</main>
        <Footer siteSettings={siteSettings} />
      </div>
    </>
  );
};

export default Layout; 